<template>
  <!-- DF--Virtual-Nurse-IP: hero + 532px rail; named nurse = overlapping avatar on rail -->
  <v-container fluid class="pa-0 fill-height bg-backgroundBase vc-incoming-root">
    <PatientBackground />

    <v-fade-transition appear>
      <div v-show="mounted" class="vc-incoming-hero">
        <h1 class="vc-incoming-hero__title text-wrap">{{ nurseCallingLine }}</h1>
        <p class="vc-incoming-hero__purpose text-wrap">{{ callPurpose }}</p>
      </div>
    </v-fade-transition>

    <v-fade-transition appear>
      <v-sheet
        v-show="mounted"
        class="d-flex flex-column bg-white vc-incoming-panel"
        :class="{ 'vc-incoming-panel--with-avatar': showNurseAvatar }"
        elevation="24"
        height="100%"
        rounded="0"
      >
        <div v-if="showNurseAvatar" class="vc-rail__avatar-wrap" aria-hidden="true">
          <v-avatar class="vc-rail__avatar" size="120">
            {{ avatarInitials }}
          </v-avatar>
        </div>

        <div :class="showNurseAvatar ? 'mb-10' : 'mb-16'">
          <v-icon class="vc-rail__video-icon" color="primary">mdi-video-plus-outline</v-icon>
        </div>

        <div class="flex-grow-1" aria-hidden="true" />

        <div class="vc-rail-cta-stack mt-auto pt-2">
          <VBtnPrimary
            block
            class="vc-incoming-action text-none"
            size="x-large"
            @click="handleAccept"
          >
            Accept
            <v-icon end size="large">mdi-check</v-icon>
          </VBtnPrimary>

          <VBtnTextDark
            block
            class="vc-incoming-decline text-none"
            size="x-large"
            @click="handleDecline"
          >
            Decline
            <v-icon end color="dark" size="large">mdi-close</v-icon>
          </VBtnTextDark>
        </div>
      </v-sheet>
    </v-fade-transition>
  </v-container>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useCallStore } from '../stores/callStore'
import { signalingService } from '../services/signalingService'
import { apiClient } from '../services/apiClient'
import PatientBackground from '../components/PatientBackground.vue'
import type { SessionCredentials } from '@virtual-care/shared'
import { incomingCallHeadline, incomingPurposeLine } from '@virtual-care/shared'
import { useNurseAvatarInitials } from '../composables/useNurseAvatarInitials'

interface Props {
  sessionId: string
}

const props = defineProps<Props>()
const router = useRouter()
const callStore = useCallStore()
const mounted = ref(false)

const resolvedSessionId = computed(() => callStore.sessionId ?? props.sessionId)

const nurseNameForAvatar = computed(() => callStore.nurseName)
const avatarInitials = useNurseAvatarInitials(nurseNameForAvatar)

/** Figma: avatar only when showing a specific nurse (not generic headline). */
const showNurseAvatar = computed(
  () => !callStore.useGenericNurseLabel && !!callStore.nurseName?.trim()
)

const nurseCallingLine = computed(() =>
  incomingCallHeadline({
    nurseName: callStore.nurseName,
    useGenericNurseLabel: callStore.useGenericNurseLabel,
  })
)

const callPurpose = computed(() => incomingPurposeLine(callStore.callPurpose))

const handleAccept = async () => {
  const sessionId = resolvedSessionId.value
  const roomId = apiClient.getRoomId()

  try {
    const joinResponse = await apiClient.joinSession(sessionId, 'patient')

    console.log('[IncomingCallView] Joined session', {
      provider: joinResponse.session?.provider,
      hasCredentials: !!joinResponse.session?.credentials
    })

    const provider = joinResponse.session?.provider
    if (!provider) {
      throw new Error('No video provider returned from backend')
    }
    
    const credentials = joinResponse.session?.credentials

    callStore.acceptCall(
      provider as never,
      credentials as SessionCredentials | undefined
    )

    signalingService.notifyCallAccepted(sessionId, roomId)

    callStore.setStatus('connecting')
    router.push(`/connecting/${sessionId}`)
  } catch (err) {
    console.error('[IncomingCallView] Failed to accept call:', err)
    callStore.endCall()
    router.push('/')
  }
}

const handleDecline = async () => {
  const sessionId = resolvedSessionId.value
  try { await apiClient.declineSession(sessionId) } catch { /* best-effort */ }
  callStore.endCall()
  router.push('/decline-confirm')
}

const handleCallEnded = async () => {
  console.log('[IncomingCallView] handleCallEnded called, sessionId:', resolvedSessionId.value)
  const sessionId = resolvedSessionId.value
  
  // Mark session as missed and update store state
  try { await apiClient.markSessionMissed(sessionId) } catch (err) { 
    console.warn('[IncomingCallView] markSessionMissed failed:', err)
  }
  
  // Set status to missed to persist state
  callStore.setStatus('missed')
  
  console.log('[IncomingCallView] Routing to /missed/', sessionId)
  // Pass sessionId to missed view so it can access session data even if store is cleared
  router.push(`/missed/${sessionId}`)
}

onMounted(() => {
  mounted.value = true
  signalingService.on('call_ended', handleCallEnded)
})

onBeforeUnmount(() => {
  signalingService.off('call_ended', handleCallEnded)
})
</script>
